import * as React from 'react'
import * as Form from 'primitives/form'
import { SpinnerIcon } from 'primitives/icons'
import * as Sentry from '@sentry/nextjs'
import Image from 'next/image'
import { useUserQuery } from 'auth/hooks'
import { useTranslations } from 'next-intl'
import { keepPreviousData } from '@tanstack/react-query'

import { useContentCreateReviewMutation, useContentEditReviewMutation } from '@web/gen/content'
import { useReviewId } from './context'
import { useProductId } from '@web/app/[locale]/(web)/prod/[slug]/product-id-context'

interface ModalProps {
  onClose: () => void
  setError: React.Dispatch<React.SetStateAction<string | null>>
  isNewComment?: boolean
}

export function ModalForm({ onClose, setError, isNewComment }: ModalProps) {
  const productId = useProductId()
  const reviewId = useReviewId()
  const t = useTranslations('prod.product_description_page.reviews.modal_reply')
  const createReviewMutation = useContentCreateReviewMutation()
  const editReviewMutation = useContentEditReviewMutation()
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const formRef = React.useRef<HTMLFormElement>(null)
  const { data: userData } = useUserQuery(undefined, {
    placeholderData: keepPreviousData,
    select: data => data.me?.user,
  })

  const isFormInvalid = () => {
    if (!formRef.current) {
      return
    }
    const formData = new FormData(formRef.current)
    const details = formData.get('details') as string
    const consent = formData.get('consent') === 'on'
    const isFormValid = details && consent

    return !isFormValid
  }

  const getFormData = () => {
    if (!formRef.current) {
      return
    }
    return new FormData(formRef.current)
  }

  const handleFormValidation = () => {
    if (isFormInvalid()) {
      setError(t('required'))
      return
    }
    setError(null)
  }

  const handleFromSubmit: React.FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault()
    const formData = getFormData()
    const isInvalid = isFormInvalid()

    if (!productId || !reviewId) {
      return
    }

    if (isInvalid) {
      handleFormValidation()
    } else {
      setIsSubmitting(true)

      isNewComment
        ? createReviewMutation
            .mutateAsync({
              productId,
              body: formData?.get('details') as string,
              parentId: reviewId,
            })
            .then(() => {
              onClose()
            })
            .catch(error => {
              Sentry.captureException(
                new Error(
                  'Something went wrong when creating a reply to a review:' +
                    (error as Error)?.message
                    ? (error as Error)?.message
                    : (error as string),
                ),
              )
              setError((error as Error)?.message ? (error as Error)?.message : (error as string))
            })
            .finally(() => {
              setIsSubmitting(false)
            })
        : editReviewMutation
            .mutateAsync({
              reviewId,
              body: formData?.get('details') as string,
            })
            .then(() => {
              onClose()
            })
            .catch(error => {
              Sentry.captureException(
                new Error(
                  'Something went wrong when editing a reply to a review:' +
                    (error as Error)?.message
                    ? (error as Error)?.message
                    : (error as string),
                ),
              )
              setError((error as Error)?.message ? (error as Error)?.message : (error as string))
            })
            .finally(() => {
              setIsSubmitting(false)
            })
    }
  }

  return (
    <Form.Root
      ref={formRef}
      onSubmit={handleFromSubmit}
      onInvalid={() => {
        handleFormValidation()
      }}
      className="flex w-full flex-col gap-4"
    >
      <h2 className="text-24 font-bold">{t('title')}</h2>
      <div className="flex items-center text-[16px]">
        {userData?.avatarUrl ? (
          <Image
            alt="avatar"
            src={userData?.avatarUrl}
            width={28}
            height={28}
            className="mr-2 rounded-full"
          />
        ) : null}
        {userData?.firstName} {userData?.lastName}
      </div>
      <div className="mb-2">
        {t.rich('author', {
          b: () => <span className="font-semibold">{'Test user'}</span>,
        })}
      </div>
      <div className="flex flex-col gap-2">
        <div className="w-full">
          <Form.Field name="productURL" aria-disabled>
            <Form.Label className="text-14 mb-2 inline-block align-text-top data-[invalid='true']:text-red-100">
              {t.rich('review', {
                b: str => <span className="font-semibold">{str}</span>,
              })}
            </Form.Label>
            <Form.Control asChild required>
              <textarea
                required
                autoComplete="details"
                name="details"
                id="details"
                rows={6}
                disabled={false}
                onChange={() => {
                  setError(null)
                }}
                placeholder="Type your reply"
                className="text-14 h placeholder:text-cf-dreamy-cloud-800 rounded-2 relative flex h-auto w-full border border-[#e0e2ea] bg-transparent p-3 outline-none ring-transparent disabled:opacity-60 data-[invalid='true']:border-red-100"
              />
            </Form.Control>
          </Form.Field>
        </div>

        <Form.Submit
          onInvalid={() => {
            handleFormValidation()
            setError(t('modal_review.required'))
          }}
          asChild
        >
          <button
            disabled={false}
            className=" text-service-white text-14 rounded-2 mb-2 mt-2 flex h-[45px] w-full items-center justify-center bg-[#403068] font-medium transition-[background-color] disabled:cursor-not-allowed disabled:bg-[#403068] disabled:opacity-75"
          >
            {isSubmitting ? (
              <SpinnerIcon className="h-[24px] w-[24px] animate-spin" />
            ) : (
              t('submit')
            )}
          </button>
        </Form.Submit>
        <Form.Field name="consent">
          <Form.Label
            className="text-14 flex items-center align-text-top data-[invalid='true']:text-red-100"
            htmlFor="consent"
          >
            <Form.Control asChild required>
              <input
                type="checkbox"
                required
                defaultChecked={true}
                name="consent"
                id="consent"
                onChange={() => {
                  setError(null)
                }}
                className="mr-2 accent-[#403068] data-[invalid='true']:border-red-100"
              />
            </Form.Control>
            {t('consent')}
          </Form.Label>
        </Form.Field>
      </div>
    </Form.Root>
  )
}
